import i18n from "i18next";
import { MasterIndex } from "verdi-web-client";
import { AreaOfInterest } from "verdi-web-client/Models";
import { mutationInvalidationTargets } from "@/api/mutation-invalidation-targets";
import { createSite, deleteSite, updateSite } from "@/api/orval/generated/site";
import type { SiteCreate, SiteUpdate, VertexInfoCreate } from "@/api/orval/generated/schemas";
import { queryClient } from "@/api/queryClient";
import { toastManager } from "@/components/ui/toast";
import { NAMESPACES } from "@/i18n/constants";

type LatLng = { lat: number; lng: number };

// The V2 bodies are strict, so map vertices must not carry extra keys.
export const toVertices = (polygon: LatLng[]): VertexInfoCreate[] =>
    polygon.map(({ lat, lng }) => ({ lat, lng, depth: 0 }));

const invalidate = (operationId: "createSite" | "updateSite" | "deleteSite") =>
    Promise.all(
        mutationInvalidationTargets[operationId].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );

// A category only exists once something is filed under it.
const localAreaOfInterest = (siteVerdiId: string): AreaOfInterest | undefined =>
    MasterIndex.aoi?.byAssetId[siteVerdiId];

// Never fall back to the Mongo id.
const requireSiteVerdiId = (siteVerdiId: string | undefined): string => {
    if (!siteVerdiId) {
        throw new Error("Site has no verdiId");
    }
    return siteVerdiId;
};

export const showAreaOfInterestWriteFailure = (copyKey: "save-failed" | "delete-failed") => {
    toastManager.add({
        title: i18n.t(`area-of-interest-writes.${copyKey}`, { ns: NAMESPACES.COMMON }),
        type: "error",
    });
};

export const createAreaOfInterest = async (data: SiteCreate): Promise<AreaOfInterest> => {
    const created = await createSite(data);
    // The constructor registers the model in MasterIndex.
    const aoi = new AreaOfInterest(created);
    await invalidate("createSite");
    return aoi;
};

export const updateAreaOfInterest = async (siteVerdiId: string | undefined, data: SiteUpdate): Promise<void> => {
    const verdiId = requireSiteVerdiId(siteVerdiId);
    const updated = await updateSite(verdiId, data);
    const aoi = localAreaOfInterest(verdiId);
    if (aoi) {
        aoi.loadFromJSON(updated);
        aoi.onChange.trigger({ target: aoi });
    }
    await invalidate("updateSite");
};

export const deleteAreaOfInterest = async (siteVerdiId: string | undefined): Promise<void> => {
    const verdiId = requireSiteVerdiId(siteVerdiId);
    await deleteSite(verdiId);
    // ModelBase.delete only removes the local copy.
    await localAreaOfInterest(verdiId)?.delete();
    await invalidate("deleteSite");
};
